import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, FileText, CheckSquare, Square, Loader2, Search } from "lucide-react";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}

/**
 * CreateStatementModal
 * Props:
 *   customerId - OPTIONAL. When supplied, modal is scoped to that customer.
 *                When null/undefined, the modal renders a customer picker
 *                so it can be used from the global /admin/statements page.
 *   open       - boolean
 *   onClose()  - close handler
 *   onCreated(statement) - success callback
 */
export default function CreateStatementModal({ customerId, open, onClose, onCreated }) {
    const navigate = useNavigate();
    // Phase K.1b — when customerId is not provided, allow the operator to
    // pick one inline. The rest of the flow keys off ``activeCustomerId``.
    const [pickedCustomerId, setPickedCustomerId] = useState(null);
    const activeCustomerId = customerId || pickedCustomerId;
    const [custQuery, setCustQuery] = useState("");
    const [custResults, setCustResults] = useState([]);
    const [custLoading, setCustLoading] = useState(false);

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [autoAll, setAutoAll] = useState(false);
    const [periodStart, setPeriodStart] = useState("");
    const [periodEnd, setPeriodEnd] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    // Reset picker + selections when modal closes/reopens.
    useEffect(() => {
        if (!open) {
            setPickedCustomerId(null);
            setCustQuery("");
            setCustResults([]);
            setSelected(new Set());
            setAutoAll(false);
            setInvoices([]);
            setPeriodStart("");
            setPeriodEnd("");
            setNotes("");
        }
    }, [open]);

    // Customer picker: debounced search (only when we don't already have a customer).
    useEffect(() => {
        if (!open || customerId || pickedCustomerId) return;
        const t = setTimeout(async () => {
            setCustLoading(true);
            try {
                const { data } = await api.get("/admin/customers", {
                    params: { q: custQuery, limit: 25 },
                });
                setCustResults(data.items || []);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setCustLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [open, customerId, pickedCustomerId, custQuery]);

    // Load outstanding invoices once we have an active customer.
    useEffect(() => {
        if (!open || !activeCustomerId) return;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/admin/invoices`, {
                    params: {
                        customer_id: activeCustomerId,
                        limit: 500,
                    },
                });
                const list = (data.items || []).filter((i) => {
                    const eligible = ["draft", "sent", "overdue"].includes(i.status);
                    const linked = !!i.statement_id;
                    const residual =
                        (i.total_cents || 0) - (i.amount_paid_cents || 0);
                    return eligible && !linked && residual > 0;
                });
                setInvoices(list);
                setSelected(new Set());
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [open, activeCustomerId]);

    // Auto-toggle: select all or clear.
    useEffect(() => {
        if (autoAll) {
            setSelected(new Set(invoices.map((i) => i.id)));
        }
    }, [autoAll, invoices]);

    const toggle = (id) => {
        setAutoAll(false);
        setSelected((prev) => {
            const nx = new Set(prev);
            if (nx.has(id)) nx.delete(id);
            else nx.add(id);
            return nx;
        });
    };

    const totalCents = useMemo(() => {
        return invoices
            .filter((i) => selected.has(i.id))
            .reduce(
                (sum, i) =>
                    sum + Math.max(0, (i.total_cents || 0) - (i.amount_paid_cents || 0)),
                0
            );
    }, [invoices, selected]);

    const submit = async () => {
        if (!activeCustomerId) {
            toast.error("Select a customer first.");
            return;
        }
        if (selected.size === 0 && !autoAll) {
            toast.error("Select at least one invoice.");
            return;
        }
        setBusy(true);
        try {
            const body = {
                period_start: periodStart || null,
                period_end: periodEnd || null,
                notes: notes || null,
            };
            if (autoAll && selected.size === invoices.length) {
                body.auto_collect = true;
            } else {
                body.invoice_ids = Array.from(selected);
            }
            const { data } = await api.post(
                `/admin/customers/${activeCustomerId}/statements`,
                body
            );
            toast.success("Statement created.");
            if (onCreated) onCreated(data);
            onClose();
            navigate(`/admin/statements/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <Card
                className="w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
                data-testid="create-statement-modal"
            >
                <CardContent className="pt-6 space-y-4 flex-1 overflow-y-auto">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold flex items-center gap-2">
                            <FileText className="h-5 w-5" /> Create consolidated statement
                        </h2>
                        <Button variant="ghost" size="sm" onClick={onClose}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Phase K.1b — customer picker (only when not scoped). */}
                    {!customerId && !pickedCustomerId && (
                        <div className="space-y-2" data-testid="statement-customer-picker">
                            <label className="text-xs text-slate-500">Customer</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    placeholder="Search by company name, DBA, EIN, USDOT, email…"
                                    className="pl-9"
                                    value={custQuery}
                                    onChange={(e) => setCustQuery(e.target.value)}
                                    data-testid="statement-customer-search"
                                    autoFocus
                                />
                            </div>
                            <div className="border rounded max-h-64 overflow-y-auto bg-white">
                                {custLoading && (
                                    <div className="py-6 text-center text-slate-500 flex items-center justify-center gap-2 text-sm">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                                    </div>
                                )}
                                {!custLoading && custResults.length === 0 && (
                                    <div className="py-6 text-center text-slate-500 text-sm">
                                        {custQuery ? "No matching customers." : "Start typing to search customers."}
                                    </div>
                                )}
                                {custResults.map((cu) => (
                                    <button
                                        type="button"
                                        key={cu.id}
                                        onClick={() => setPickedCustomerId(cu.id)}
                                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0"
                                        data-testid={`statement-customer-option-${cu.id}`}
                                    >
                                        <div className="font-medium text-slate-800">
                                            {cu.company_name || cu.dba || cu.id.slice(0, 8)}
                                        </div>
                                        <div className="text-xs text-slate-500 flex gap-3">
                                            {cu.dba && cu.dba !== cu.company_name && <span>DBA: {cu.dba}</span>}
                                            {cu.usdot_number && <span>USDOT {cu.usdot_number}</span>}
                                            {cu.billing_contact_email && <span>{cu.billing_contact_email}</span>}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Selected customer chip when picked (not scoped by prop). */}
                    {!customerId && pickedCustomerId && (
                        <div className="flex items-center gap-2 p-2 rounded bg-slate-50 border">
                            <span className="text-xs text-slate-500 uppercase tracking-wider">Customer</span>
                            <span className="font-mono text-xs text-slate-700">
                                {pickedCustomerId.slice(0, 12)}…
                            </span>
                            <Button
                                variant="ghost" size="sm" className="ml-auto"
                                onClick={() => {
                                    setPickedCustomerId(null);
                                    setInvoices([]);
                                    setSelected(new Set());
                                }}
                                data-testid="statement-customer-clear"
                            >
                                Change
                            </Button>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-500">Period start</label>
                            <Input
                                type="date"
                                value={periodStart}
                                onChange={(e) => setPeriodStart(e.target.value)}
                                disabled={!activeCustomerId}
                                data-testid="statement-period-start"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-500">Period end</label>
                            <Input
                                type="date"
                                value={periodEnd}
                                onChange={(e) => setPeriodEnd(e.target.value)}
                                disabled={!activeCustomerId}
                                data-testid="statement-period-end"
                            />
                        </div>
                    </div>

                    {activeCustomerId && (
                    <>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAutoAll(!autoAll)}
                            data-testid="statement-select-all-toggle"
                        >
                            {autoAll ? (
                                <>
                                    <CheckSquare className="h-4 w-4 mr-1" /> All selected
                                </>
                            ) : (
                                <>
                                    <Square className="h-4 w-4 mr-1" /> Select all outstanding
                                </>
                            )}
                        </Button>
                        <span className="text-sm text-slate-500">
                            {invoices.length} eligible invoice(s)
                        </span>
                    </div>

                    <div className="border rounded max-h-80 overflow-y-auto">
                        {loading && (
                            <div className="py-8 text-center text-slate-500 flex items-center justify-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                            </div>
                        )}
                        {!loading && invoices.length === 0 && (
                            <div className="py-8 text-center text-slate-500 text-sm">
                                No outstanding invoices for this customer.
                            </div>
                        )}
                        {!loading && invoices.length > 0 && (
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-xs text-slate-500 uppercase sticky top-0">
                                    <tr>
                                        <th className="w-8 px-2" />
                                        <th className="text-left px-2 py-2">Invoice #</th>
                                        <th className="text-left px-2 py-2">Status</th>
                                        <th className="text-left px-2 py-2">Due</th>
                                        <th className="text-right px-2 py-2">Amount</th>
                                        <th className="text-left px-2 py-2">Flags</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="create-statement-invoice-list">
                                    {invoices.map((inv) => {
                                        const residual =
                                            (inv.total_cents || 0) -
                                            (inv.amount_paid_cents || 0);
                                        return (
                                            <tr
                                                key={inv.id}
                                                className="border-t hover:bg-slate-50 cursor-pointer"
                                                onClick={() => toggle(inv.id)}
                                                data-testid={`create-statement-row-${inv.id}`}
                                            >
                                                <td className="px-2 py-1">
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(inv.id)}
                                                        onChange={() => toggle(inv.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        data-testid={`create-statement-checkbox-${inv.id}`}
                                                    />
                                                </td>
                                                <td className="px-2 py-1 font-mono text-xs">
                                                    {inv.invoice_number ||
                                                        inv.id.slice(0, 8)}
                                                </td>
                                                <td className="px-2 py-1">
                                                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100">
                                                        {inv.status}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-1 text-slate-600 text-xs">
                                                    {inv.due_date || "—"}
                                                </td>
                                                <td className="px-2 py-1 text-right">
                                                    {fmtMoney(residual)}
                                                </td>
                                                <td className="px-2 py-1">
                                                    {(inv.prorated || inv.is_backfill) && (
                                                        <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800">
                                                            prorated
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div>
                        <label className="text-xs text-slate-500">Notes (optional)</label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Internal notes for the audit trail"
                            data-testid="statement-notes-input"
                        />
                    </div>
                    </>
                    )}
                </CardContent>

                <div className="border-t p-4 flex items-center justify-between bg-slate-50">
                    <div className="text-sm text-slate-700">
                        Preview total:{" "}
                        <strong
                            className="text-lg text-slate-900"
                            data-testid="create-statement-total-preview"
                        >
                            {fmtMoney(totalCents)}
                        </strong>{" "}
                        <span className="text-xs text-slate-500">
                            ({selected.size} invoice{selected.size === 1 ? "" : "s"})
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={onClose} disabled={busy}>
                            Cancel
                        </Button>
                        <Button
                            onClick={submit}
                            disabled={busy || !activeCustomerId || (selected.size === 0 && !autoAll)}
                            data-testid="create-statement-submit"
                        >
                            {busy ? "Creating…" : "Create statement"}
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}
